/**
 * StatsService — shared statistical primitives for the Figures plugin.
 *
 * Wraps simple-statistics + a few hand-rolled helpers so ChartService and
 * PlotConfigurator can call these without duplicating logic. All functions
 * accept plain numeric arrays (NaN/null/undefined safe — they're filtered
 * out before any math).
 */

// All statistical helpers are implemented inline so this service has zero
// runtime dependencies. The project already has a transitive
// `simple-statistics@0.9.2` but its API is snake_case and incomplete for
// our needs — safer to own these tiny primitives.

const ss = {
  sum (arr) { let s = 0; for (const v of arr) s += v; return s },
  mean (arr) { return arr.length ? ss.sum(arr) / arr.length : NaN },
  variance (arr) {
    if (arr.length < 2) return 0
    const m = ss.mean(arr)
    let s = 0
    for (const v of arr) s += (v - m) * (v - m)
    return s / arr.length
  },
  standardDeviation (arr) { return Math.sqrt(ss.variance(arr)) },
  sampleVariance (arr) {
    if (arr.length < 2) return 0
    const m = ss.mean(arr)
    let s = 0
    for (const v of arr) s += (v - m) * (v - m)
    return s / (arr.length - 1)
  },
  sampleStandardDeviation (arr) { return Math.sqrt(ss.sampleVariance(arr)) },
  medianSorted (sorted) {
    if (!sorted.length) return NaN
    const n = sorted.length
    return n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2
  },
  // Linear interpolated quantile (type 7 / R default) on a pre-sorted array.
  quantileSorted (sorted, p) {
    if (!sorted.length) return NaN
    if (sorted.length === 1) return sorted[0]
    const pos = p * (sorted.length - 1)
    const lo = Math.floor(pos)
    const hi = Math.ceil(pos)
    if (lo === hi) return sorted[lo]
    const t = pos - lo
    return sorted[lo] * (1 - t) + sorted[hi] * t
  },
  // Ordinary-least-squares y = mx + b. Input: [[x, y], ...].
  linearRegression (pairs) {
    const n = pairs.length
    if (n < 2) return { m: 0, b: 0 }
    let sx = 0; let sy = 0; let sxy = 0; let sxx = 0
    for (const [x, y] of pairs) { sx += x; sy += y; sxy += x * y; sxx += x * x }
    const denom = n * sxx - sx * sx
    const m = denom === 0 ? 0 : (n * sxy - sx * sy) / denom
    const b = (sy - m * sx) / n
    return { m, b }
  },
  // Pearson correlation between two equal-length numeric arrays.
  sampleCorrelation (xs, ys) {
    const n = Math.min(xs.length, ys.length)
    if (n < 2) return 0
    let sx = 0; let sy = 0; let sxy = 0; let sxx = 0; let syy = 0
    for (let i = 0; i < n; i++) {
      sx += xs[i]; sy += ys[i]; sxy += xs[i] * ys[i]
      sxx += xs[i] * xs[i]; syy += ys[i] * ys[i]
    }
    const num = n * sxy - sx * sy
    const den = Math.sqrt((n * sxx - sx * sx) * (n * syy - sy * sy))
    return den === 0 ? 0 : num / den
  }
}

function cleanNumbers (arr) {
  const out = []
  for (const v of arr) {
    if (v === null || v === undefined || v === '') continue
    const n = typeof v === 'number' ? v : Number(v)
    if (Number.isFinite(n)) out.push(n)
  }
  return out
}

/**
 * Summary stats for a numeric array.
 * Returns nulls where undefined to keep shape consistent for tables.
 */
export function summarize (rawArr) {
  const data = cleanNumbers(rawArr)
  if (!data.length) {
    return { count: 0, n: 0, validCount: 0, missing: rawArr ? rawArr.length : 0, mean: null, median: null, min: null, max: null, stddev: null, variance: null, p10: null, p25: null, p75: null, p90: null, iqr: null, range: null, sum: null }
  }
  const sorted = [...data].sort((a, b) => a - b)
  const mean = ss.mean(data)
  const stddev = data.length > 1 ? ss.standardDeviation(data) : 0
  return {
    count: data.length,
    n: data.length,
    validCount: data.length,
    missing: (rawArr ? rawArr.length : data.length) - data.length,
    mean,
    median: ss.medianSorted(sorted),
    min: sorted[0],
    max: sorted[sorted.length - 1],
    stddev,
    variance: data.length > 1 ? ss.variance(data) : 0,
    p10: ss.quantileSorted(sorted, 0.10),
    p25: ss.quantileSorted(sorted, 0.25),
    p75: ss.quantileSorted(sorted, 0.75),
    p90: ss.quantileSorted(sorted, 0.90),
    iqr: ss.quantileSorted(sorted, 0.75) - ss.quantileSorted(sorted, 0.25),
    range: sorted[sorted.length - 1] - sorted[0],
    sum: ss.sum(data)
  }
}

/**
 * Linear regression y = mx + b. Returns {slope, intercept, r, r2}.
 * Null if fewer than 2 pairs.
 */
export function linearRegression (xs, ys) {
  const pairs = []
  for (let i = 0; i < xs.length; i++) {
    const x = Number(xs[i])
    const y = Number(ys[i])
    if (Number.isFinite(x) && Number.isFinite(y)) pairs.push([x, y])
  }
  if (pairs.length < 2) return null
  const { m, b } = ss.linearRegression(pairs)
  const r = ss.sampleCorrelation(pairs.map(p => p[0]), pairs.map(p => p[1]))
  return { slope: m, intercept: b, r, r2: r * r, n: pairs.length }
}

/**
 * Pearson correlation between two numeric arrays.
 */
export function correlation (xs, ys) {
  const xc = []
  const yc = []
  for (let i = 0; i < xs.length; i++) {
    const x = Number(xs[i])
    const y = Number(ys[i])
    if (Number.isFinite(x) && Number.isFinite(y)) { xc.push(x); yc.push(y) }
  }
  if (xc.length < 2) return 0
  return ss.sampleCorrelation(xc, yc)
}

/**
 * Bin values into a histogram. Returns { bins: [{x0, x1, count, midpoint}] }.
 * binCount defaults to Freedman–Diaconis heuristic, capped at 60.
 */
export function histogram (rawArr, binCount = null) {
  const data = cleanNumbers(rawArr)
  if (!data.length) return { bins: [], edges: [] }

  const sorted = [...data].sort((a, b) => a - b)
  const min = sorted[0]
  const max = sorted[sorted.length - 1]
  if (min === max) {
    return { bins: [{ x0: min, x1: min, count: data.length, midpoint: min }], edges: [min] }
  }

  let bins = binCount
  if (!bins) {
    const iqr = ss.quantileSorted(sorted, 0.75) - ss.quantileSorted(sorted, 0.25)
    const fdWidth = 2 * iqr * Math.pow(data.length, -1 / 3)
    bins = fdWidth > 0
      ? Math.max(5, Math.min(60, Math.ceil((max - min) / fdWidth)))
      : Math.max(5, Math.min(30, Math.ceil(Math.sqrt(data.length))))
  }
  bins = Math.max(2, Math.min(100, Math.floor(bins)))

  const width = (max - min) / bins
  const out = []
  for (let i = 0; i < bins; i++) {
    out.push({ x0: min + i * width, x1: min + (i + 1) * width, count: 0, midpoint: min + (i + 0.5) * width })
  }
  for (const v of data) {
    let idx = Math.floor((v - min) / width)
    if (idx >= bins) idx = bins - 1
    out[idx].count++
  }
  return { bins: out, edges: out.map(b => b.x0).concat([max]), binWidth: width, min, max }
}

/**
 * Gaussian Kernel Density Estimate sampled at `samples` points across the
 * range of the data. Silverman's rule for bandwidth by default.
 */
export function kernelDensity (rawArr, { samples = 100, bandwidth = null } = {}) {
  const data = cleanNumbers(rawArr)
  if (data.length < 2) return []
  const sorted = [...data].sort((a, b) => a - b)
  const min = sorted[0]
  const max = sorted[sorted.length - 1]
  if (min === max) return [{ x: min, y: 1 }]

  const sd = ss.standardDeviation(data)
  const h = bandwidth || (1.06 * sd * Math.pow(data.length, -1 / 5))
  if (!Number.isFinite(h) || h <= 0) return []

  const pad = (max - min) * 0.05
  const step = (max - min + pad * 2) / (samples - 1)
  const out = []
  const coeff = 1 / (data.length * h * Math.sqrt(2 * Math.PI))
  for (let i = 0; i < samples; i++) {
    const x = min - pad + step * i
    let y = 0
    for (const v of data) {
      const u = (x - v) / h
      y += Math.exp(-0.5 * u * u)
    }
    out.push({ x, y: y * coeff })
  }
  return out
}

/**
 * Box-plot statistics for a numeric array: {min, q1, median, q3, max, outliers}.
 * Uses the 1.5 × IQR convention.
 */
export function boxplotStats (rawArr) {
  const data = cleanNumbers(rawArr)
  if (!data.length) return null
  const sorted = [...data].sort((a, b) => a - b)
  const q1 = ss.quantileSorted(sorted, 0.25)
  const q3 = ss.quantileSorted(sorted, 0.75)
  const iqr = q3 - q1
  const lowerFence = q1 - 1.5 * iqr
  const upperFence = q3 + 1.5 * iqr
  let min = Infinity
  let max = -Infinity
  const outliers = []
  for (const v of sorted) {
    if (v < lowerFence || v > upperFence) outliers.push(v)
    else {
      if (v < min) min = v
      if (v > max) max = v
    }
  }
  return {
    min: Number.isFinite(min) ? min : sorted[0],
    q1,
    median: ss.medianSorted(sorted),
    q3,
    max: Number.isFinite(max) ? max : sorted[sorted.length - 1],
    outliers,
    n: data.length
  }
}

/**
 * Group an array of records by a key, returning { [groupKey]: Row[] }.
 */
export function groupBy (rows, keyField) {
  const out = {}
  for (const r of rows || []) {
    const k = r[keyField]
    const label = (k === null || k === undefined || k === '') ? '—' : String(k)
    if (!out[label]) out[label] = []
    out[label].push(r)
  }
  return out
}

/**
 * Format a number for display — compact enough for chart labels.
 */
export function fmt (v, digits = 3) {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const abs = Math.abs(v)
  if (abs === 0) return '0'
  if (abs < 0.001 || abs >= 1e6) return v.toExponential(digits)
  return Number(v.toPrecision(digits + 1)).toString()
}

/**
 * Classify a column as 'number' | 'date' | 'text' based on samples.
 * Used to drive axis type defaults (linear vs. time) in Chart.js.
 */
export function inferColumnType (values) {
  const sample = (values || []).filter(v => v !== null && v !== undefined && v !== '').slice(0, 50)
  if (!sample.length) return 'text'
  let numeric = 0
  let dateLike = 0
  for (const v of sample) {
    const n = typeof v === 'number' ? v : Number(v)
    if (Number.isFinite(n)) numeric++
    const d = v instanceof Date ? v : new Date(v)
    if (!Number.isNaN(d.getTime()) && typeof v === 'string' && /\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4}/.test(v)) dateLike++
  }
  if (dateLike / sample.length > 0.7) return 'date'
  if (numeric / sample.length > 0.8) return 'number'
  return 'text'
}

export default {
  summarize,
  linearRegression,
  correlation,
  histogram,
  kernelDensity,
  boxplotStats,
  groupBy,
  fmt,
  inferColumnType
}
